import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../../../components/Panel'
import { deskApi, type AlertRuleView, type DeskPub, type RecipientKind, type RecipientTitle } from '../../../api/visitorsDesk'
import { pubLabel } from '../../../lib/deskFormat'
import { domainError, initialPicked, normaliseDomain, paramError, parseTerms, recipientChoices, recipientsSummary, ruleTextPreview } from '../../../lib/deskSettings'
import { errorDetail } from '../../../lib/visitorsFormat'
import { agentApi, useAgentSettings } from '../../../api/visitorsAgent'
import { NotYetLive } from './NotYetLive'

const TITLES: DeskPub[] = ['flight_journal', 'model_airplane_news', 'rc_car_action', 'diecast']
const th = 'px-2 py-1.5 text-left font-mono text-[11px] font-semibold uppercase tracking-wide text-paper-faint'
const td = 'px-2 py-1.5 align-middle'
const field = 'min-h-[44px] md:min-h-[32px] bg-ink-800 border border-ink-500 px-2 font-mono text-[13px] text-paper'
const btn = 'min-h-[44px] md:min-h-[32px] px-3 font-mono text-[12px] border border-ink-500 text-paper-dim hover:text-paper disabled:opacity-50'
const err = 'font-mono text-[12px] text-radar-red'

function DomainsPanel() {
  const qc = useQueryClient()
  const list = useQuery({ queryKey: ['desk-settings', 'domains'], queryFn: deskApi.sourceDomains })
  const [domain, setDomain] = useState('')
  const [grp, setGrp] = useState('')
  const [label, setLabel] = useState('')
  const [local, setLocal] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)
  const groups = list.data?.groups ?? []
  const kind = grp || groups[0]?.grp || ''
  const labelOf = (g: string) => groups.find((x) => x.grp === g)?.label ?? g
  const done = () => qc.invalidateQueries({ queryKey: ['desk-settings', 'domains'] })
  const add = useMutation({
    mutationFn: () => deskApi.putSourceDomain(normaliseDomain(domain), { grp: kind, label: label.trim() || null }),
    onSuccess: () => { setDomain(''); setLabel(''); done() },
  })
  const change = useMutation({
    mutationFn: (v: { domain: string; grp: string; label?: string | null }) => deskApi.putSourceDomain(v.domain, { grp: v.grp, label: v.label }),
    onSuccess: done,
  })
  const remove = useMutation({
    mutationFn: (d: string) => deskApi.deleteSourceDomain(d),
    onSuccess: () => { setConfirming(null); done() },
  })
  const submit = () => {
    const m = domainError(domain)
    setLocal(m)
    if (!m) add.mutate()
  }
  const error = local ?? (add.error ? errorDetail(add.error) : change.error ? errorDetail(change.error) : remove.error ? errorDetail(remove.error) : null)

  return (
    <Panel label="Forum, shop and club sites" accent="none" bodyClassName="p-0">
      <div className="p-4 space-y-3">
        {list.data?.note && <p className="font-mono text-[12px] text-paper-faint">{list.data.note}</p>}
        {list.isError && <p className={err}>{errorDetail(list.error)}</p>}
        {error && <p role="alert" className={err}>{error}</p>}
      </div>
      <div className="relative overflow-x-auto">
        <table className="w-full text-[13px] text-paper">
          <thead><tr><th className={th}>Domain</th><th className={th}>Kind</th><th className={th}>Label</th><th className={th}><span className="sr-only">Remove</span></th></tr></thead>
          <tbody>
            {(list.data?.domains ?? []).map((d) => (
              <tr key={d.domain} className="border-t border-ink-600">
                <td className={`${td} font-mono`}>{d.domain}</td>
                <td className={td}>
                  <select aria-label={`Kind for ${d.domain}`} className={field} value={d.grp}
                    onChange={(e) => change.mutate({ domain: d.domain, grp: e.target.value, label: d.label })}>
                    {!groups.some((g) => g.grp === d.grp) && <option value={d.grp}>{labelOf(d.grp)}</option>}
                    {groups.map((g) => <option key={g.grp} value={g.grp}>{g.label}</option>)}
                  </select>
                </td>
                <td className={`${td} text-paper-dim`}>{d.label || '—'}</td>
                <td className={`${td} whitespace-nowrap`}>
                  {confirming === d.domain ? (
                    <span className="flex gap-2">
                      <button type="button" className={`${btn} text-radar-red`} disabled={remove.isPending} onClick={() => remove.mutate(d.domain)}>Confirm remove</button>
                      <button type="button" className={btn} onClick={() => setConfirming(null)}>Cancel</button>
                    </span>
                  ) : (
                    <button type="button" className={btn} aria-label={`Remove ${d.domain}`} onClick={() => setConfirming(d.domain)}>Remove</button>
                  )}
                </td>
              </tr>
            ))}
            {list.data && list.data.domains.length === 0 && (
              <tr className="border-t border-ink-600"><td className={`${td} text-paper-faint`} colSpan={4}>No sites listed yet.</td></tr>
            )}
            <tr className="border-t border-ink-500">
              <td className={td}>
                <input aria-label="New domain" className={`${field} w-full min-w-[160px]`} placeholder="rcgroups.com" value={domain}
                  onChange={(e) => { setDomain(e.target.value); setLocal(null) }} onKeyDown={(e) => { if (e.key === 'Enter') submit() }} />
              </td>
              <td className={td}>
                <select aria-label="New kind" className={field} value={kind} onChange={(e) => setGrp(e.target.value)}>
                  {groups.map((g) => <option key={g.grp} value={g.grp}>{g.label}</option>)}
                </select>
              </td>
              <td className={td}>
                <input aria-label="New label" className={`${field} w-full min-w-[140px]`} placeholder="Label (optional)" maxLength={120} value={label}
                  onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit() }} />
              </td>
              <td className={td}><button type="button" className={btn} disabled={add.isPending || !kind} onClick={submit}>Add</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function BrandPanel() {
  const qc = useQueryClient()
  const [pub, setPub] = useState<DeskPub>('flight_journal')
  const [draft, setDraft] = useState<string | null>(null)
  const terms = useQuery({ queryKey: ['desk-settings', 'brand', pub], queryFn: () => deskApi.brandTerms(pub) })
  const text = draft ?? (terms.data ? terms.data.terms.join('\n') : '')
  const save = useMutation({
    mutationFn: () => deskApi.putBrandTerms(pub, parseTerms(text)),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['desk-settings', 'brand', pub] }); setDraft(null) },
  })
  const parsed = parseTerms(text)
  return (
    <Panel label="Brand search terms" accent="none">
      <div className="space-y-3">
        <p className="font-mono text-[12px] text-paper-faint">Search queries containing these words count as brand searches. One per line, or separated by commas. Each title has its own list.</p>
        <label className="flex flex-wrap items-center gap-2 font-mono text-[12px] text-paper-dim">
          Title
          <select className={field} value={pub} onChange={(e) => { setPub(e.target.value as DeskPub); setDraft(null); save.reset() }}>
            {TITLES.map((t) => <option key={t} value={t}>{pubLabel(t)}</option>)}
          </select>
        </label>
        <textarea aria-label="Brand terms" rows={6} className={`${field} w-full py-2`} value={text} onChange={(e) => { setDraft(e.target.value); save.reset() }} />
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={btn} disabled={save.isPending || !terms.data}
                  onClick={() => { if (terms.data && !save.isPending) save.mutate() }}>Save</button>
          <span className="font-mono text-[12px] text-paper-faint">{parsed.length} {parsed.length === 1 ? 'term' : 'terms'}</span>
          {save.isSuccess && <span className="font-mono text-[12px] text-radar-green">Saved</span>}
        </div>
        {terms.isError && <p className={err}>{errorDetail(terms.error)}</p>}
        {save.isError && <p role="alert" className={err}>{errorDetail(save.error)}</p>}
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wide text-paper-faint">Stored for {pubLabel(pub)}</p>
          {terms.data && (terms.data.terms.length
            ? <ul className="mt-1 flex flex-wrap gap-2">{terms.data.terms.map((t) => <li key={t} className="border border-ink-500 px-1.5 py-0.5 font-mono text-[12px] text-paper-dim break-all">{t}</li>)}</ul>
            : <p className="mt-1 font-mono text-[12px] text-paper-faint">None stored.</p>)}
        </div>
      </div>
    </Panel>
  )
}

const MAIL_OFF = 'Desk email is switched off on this server. Alerts still show on Today and Trends.'
const checkRow = 'flex min-h-[44px] md:min-h-[32px] items-center gap-2 font-mono text-[12px] text-paper-dim'

function Switch({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className={checkRow}>
      <input type="checkbox" className="h-5 w-5" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-paper">{label}</span>
    </label>
  )
}

function RuleRow({ rule }: { rule: AlertRuleView }) {
  const qc = useQueryClient()
  const [on, setOn] = useState(rule.enabled)
  const [vals, setVals] = useState<Record<string, string | boolean>>(() =>
    Object.fromEntries(rule.param_specs.map((p) => [p.key, p.kind === 'bool' ? rule.params[p.key] === true : String(rule.params[p.key])])))
  const save = useMutation({
    mutationFn: () => {
      const params: Record<string, number | boolean> = {}
      for (const p of rule.param_specs) {
        const v = vals[p.key]
        params[p.key] = p.kind === 'bool' ? v === true : Number(v)
      }
      return deskApi.putAlertRule(rule.rule, { enabled: on, params })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desk-settings', 'alerts'] }),
  })
  const errors = rule.param_specs.map((p) => paramError(p, vals[p.key])).filter((m): m is string => !!m)
  const live: Record<string, number | boolean> = { ...rule.params }
  for (const p of rule.param_specs) if (!paramError(p, vals[p.key])) live[p.key] = p.kind === 'bool' ? vals[p.key] === true : Number(vals[p.key])
  const dirty = on !== rule.enabled || rule.param_specs.some((p) => (p.kind === 'bool' ? vals[p.key] !== rule.params[p.key] : Number(vals[p.key]) !== rule.params[p.key] || String(vals[p.key]).trim() === ''))
  const touch = () => save.reset()
  return (
    <tr className="border-t border-ink-600 align-top">
      <td className={td}>
        <input type="checkbox" className="h-6 w-6" aria-label={`${rule.label} on`} checked={on} onChange={(e) => { setOn(e.target.checked); touch() }} />
      </td>
      <td className={`${td} min-w-[220px]`}>
        <div className="text-paper">{rule.label}</div>
        <div className="font-mono text-[12px] text-paper-dim">{ruleTextPreview(rule.template, live)}</div>
        {rule.help && <p className="mt-1 font-mono text-[12px] text-paper-faint">{rule.help}</p>}
      </td>
      <td className={td}>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {rule.param_specs.map((p) => p.kind === 'bool' ? (
            <label key={p.key} className={checkRow}>
              <input type="checkbox" className="h-5 w-5" checked={vals[p.key] === true}
                onChange={(e) => { setVals({ ...vals, [p.key]: e.target.checked }); touch() }} />
              {p.label}
            </label>
          ) : (
            <label key={p.key} className="flex flex-col gap-1 font-mono text-[11px] text-paper-faint">
              {p.label}
              <input type="number" inputMode="decimal" className={`${field} w-28`} min={p.lo ?? undefined} max={p.hi ?? undefined}
                step={p.kind === 'int' ? 1 : 'any'} value={String(vals[p.key])}
                onChange={(e) => { setVals({ ...vals, [p.key]: e.target.value }); touch() }} />
            </label>
          ))}
        </div>
        {errors.map((m) => <p key={m} className={err}>{m}</p>)}
        {save.isError && <p role="alert" className={err}>{errorDetail(save.error)}</p>}
      </td>
      <td className={`${td} whitespace-nowrap`}>
        <button type="button" className={btn} disabled={save.isPending || errors.length > 0 || !dirty}
                onClick={() => { if (!save.isPending && errors.length === 0) save.mutate() }}>Save</button>
        {save.isSuccess && <span className="ml-2 font-mono text-[12px] text-radar-green">Saved</span>}
      </td>
    </tr>
  )
}

function AlertsPanel() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['desk-settings', 'alerts'], queryFn: deskApi.alertSettings })
  const email = useMutation({
    mutationFn: (v: boolean) => deskApi.putDigestSettings({ alerts_email_on: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desk-settings', 'alerts'] }),
  })
  const d = q.data
  return (
    <Panel label="Alerts" accent="none" bodyClassName="p-0">
      <div className="p-4 space-y-2">
        {q.isError && <p className={err}>{errorDetail(q.error)}</p>}
        {d && !d.mail_enabled && <p role="status" className="font-mono text-[12px] text-paper-dim border border-ink-500 px-3 py-2">{MAIL_OFF}</p>}
        <Switch label="Email alerts" checked={d?.alerts_email_on ?? false} disabled={!d || email.isPending}
                onChange={(v) => { if (d && !email.isPending) email.mutate(v) }} />
        {email.isError && <p role="alert" className={err}>{errorDetail(email.error)}</p>}
      </div>
      <div className="relative overflow-x-auto">
        <table className="w-full text-[13px] text-paper">
          <thead><tr><th className={th}>On</th><th className={th}>Alert</th><th className={th}>Settings</th><th className={th}><span className="sr-only">Save</span></th></tr></thead>
          <tbody>
            {(d?.rules ?? []).map((r) => <RuleRow key={`${r.rule}:${r.enabled}:${JSON.stringify(r.params)}`} rule={r} />)}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

function RecipientRow({ kind, t }: { kind: RecipientKind; t: RecipientTitle }) {
  const qc = useQueryClient()
  const [picked, setPicked] = useState<number[]>(() => initialPicked(t))
  const save = useMutation({
    mutationFn: (ids: number[]) => deskApi.putRecipients(kind, t.publication, ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desk-settings', 'recipients', kind] }),
  })
  const same = picked.length === t.user_ids.length && picked.every((id) => t.user_ids.includes(id))
  const toggle = (id: number, on: boolean) => { setPicked(on ? [...picked, id] : picked.filter((x) => x !== id)); save.reset() }
  return (
    <div className="border-t border-ink-600 px-4 py-3 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-paper">{t.title}</h3>
        <span className="font-mono text-[12px] text-paper-faint">{recipientsSummary(t)}</span>
      </div>
      <div className="flex flex-wrap gap-x-5">
        {recipientChoices(t, kind).map((c) => (
          <label key={c.user_id} className={checkRow}>
            <input type="checkbox" className="h-5 w-5" checked={picked.includes(c.user_id)} onChange={(e) => toggle(c.user_id, e.target.checked)} />
            <span className={`break-all${c.stale ? ' text-paper-faint' : ''}`}>{c.label}</span>
          </label>
        ))}
        {t.candidates.length === 0 && <p className="font-mono text-[12px] text-paper-faint">Nobody can receive this yet.</p>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={btn} disabled={save.isPending || same || picked.length === 0}
                onClick={() => { if (!save.isPending) save.mutate(picked) }}>Save</button>
        <button type="button" className={btn} disabled={save.isPending || t.default}
                onClick={() => { if (!save.isPending) { setPicked([]); save.mutate([]) } }}>Use the default (every admin)</button>
        {save.isSuccess && <span className="font-mono text-[12px] text-radar-green">Saved</span>}
      </div>
      {save.isError && <p role="alert" className={err}>{errorDetail(save.error)}</p>}
    </div>
  )
}

function RecipientList({ kind }: { kind: RecipientKind }) {
  const q = useQuery({ queryKey: ['desk-settings', 'recipients', kind], queryFn: () => deskApi.recipients(kind) })
  return (
    <div>
      {q.isError && <p className={`${err} px-4 pb-3`}>{errorDetail(q.error)}</p>}
      {(q.data?.titles ?? []).map((t) => (
        <RecipientRow key={`${t.publication}:${t.user_ids.join(',')}`} kind={kind} t={t} />
      ))}
    </div>
  )
}

function RecipientsPanel() {
  return (
    <Panel label="Who gets alerts" accent="none" bodyClassName="p-0">
      <p className="p-4 pt-3 font-mono text-[12px] text-paper-faint">Alerts cover revenue and checkout, so only admins can receive them. With nobody chosen, every admin gets them.</p>
      <RecipientList kind="alerts" />
    </Panel>
  )
}

/** `2026-10-12` as `Oct 12`, with no time-zone shift. */
function monDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

function DigestPanel() {
  const qc = useQueryClient()
  const [pub, setPub] = useState<DeskPub>('flight_journal')
  const s = useQuery({ queryKey: ['desk-settings', 'digest'], queryFn: deskApi.digestSettings })
  const prev = useQuery({ queryKey: ['desk-settings', 'digest-preview', pub], queryFn: () => deskApi.digestPreview(pub) })
  const on = useMutation({
    mutationFn: (v: boolean) => deskApi.putDigestSettings({ digest_on: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desk-settings', 'digest'] }),
  })
  const test = useMutation({ mutationFn: () => deskApi.digestTest(pub) })
  const mailOff = s.data ? !s.data.mail_enabled : false
  const p = prev.data
  const ready = !!p && p.unavailable === undefined
  return (
    <Panel label="Weekly digest" accent="none" bodyClassName="p-0">
      <div className="p-4 space-y-2">
        {s.isError && <p className={err}>{errorDetail(s.error)}</p>}
        {mailOff && <p role="status" className="font-mono text-[12px] text-paper-dim border border-ink-500 px-3 py-2">{MAIL_OFF}</p>}
        <Switch label="Send the weekly digest (Mondays 7am Eastern)" checked={s.data?.digest_on ?? false} disabled={!s.data || on.isPending}
                onChange={(v) => { if (s.data && !on.isPending) on.mutate(v) }} />
        {on.isError && <p role="alert" className={err}>{errorDetail(on.error)}</p>}
        <p className="font-mono text-[12px] text-paper-faint">Who gets each title's digest. With nobody chosen, every admin does.</p>
      </div>
      <RecipientList kind="digest" />
      <div className="border-t border-ink-600 p-4 space-y-3">
        <h3 className="text-paper">Preview</h3>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex flex-wrap items-center gap-2 font-mono text-[12px] text-paper-dim">
            Title
            <select className={field} value={pub} onChange={(e) => { setPub(e.target.value as DeskPub); test.reset() }}>
              {TITLES.map((t) => <option key={t} value={t}>{pubLabel(t)}</option>)}
            </select>
          </label>
          {ready && <span className="font-mono text-[12px] text-paper-dim">Week of {monDay(p.week_start)}</span>}
        </div>
        {prev.isError && <p className={err}>{errorDetail(prev.error)}</p>}
        {p && p.unavailable !== undefined && <NotYetLive text={p.unavailable} />}
        {ready && (
          <>
            <p className="font-mono text-[12px] text-paper"><span className="text-paper-faint">Subject </span>{p.subject}</p>
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className={btn} disabled={test.isPending || mailOff || !s.data}
                      onClick={() => { if (!test.isPending && !mailOff) test.mutate() }}>Send a test to me</button>
              {mailOff && <span className="font-mono text-[12px] text-paper-faint">{MAIL_OFF}</span>}
              {test.isSuccess && (test.data.sent
                ? <span className="font-mono text-[12px] text-radar-green">Sent to {test.data.to}</span>
                : <span className="font-mono text-[12px] text-radar-red">Not sent to {test.data.to}</span>)}
            </div>
            {test.isError && <p role="alert" className={err}>{errorDetail(test.error)}</p>}
            <div className="overflow-x-auto">
              <iframe title="Digest preview" sandbox="" srcDoc={p.html} className="w-full max-w-[680px] h-[1300px] border border-ink-500 bg-white" />
            </div>
          </>
        )}
      </div>
    </Panel>
  )
}

function AnalystPanel() {
  const qc = useQueryClient()
  const s = useAgentSettings()
  const set = useMutation({
    mutationFn: (v: boolean) => agentApi.putSettings(v),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vd-agent-settings'] }),
  })
  if (s.data == null) return null
  return (
    <Panel label="Analyst" accent="none">
      <div className="space-y-2">
        <Switch label="Analyst writes a summary every morning" checked={s.data.analyst_on} disabled={set.isPending}
                onChange={(v) => { if (!set.isPending) set.mutate(v) }} />
        {set.isError && <p role="alert" className={err}>{errorDetail(set.error)}</p>}
      </div>
    </Panel>
  )
}

/** `visitors/desk/settings`: admin-only lists the desk reads (source domains, brand terms). */
export function DeskSettings() {
  return (
    <div className="space-y-6 max-w-[1500px]">
      <h1 className="font-serif text-[28px] text-paper">Desk settings</h1>
      <DomainsPanel />
      <BrandPanel />
      <AlertsPanel />
      <RecipientsPanel />
      <DigestPanel />
      <AnalystPanel />
    </div>
  )
}
